import { useState } from "react";
import { PencilIcon, XIcon } from "lucide-react";

import { Column } from "@/components/Column";
import { Row } from "@/components/Row";
import { Text } from "@/components/Text";
import { Button } from "@ui/button";

import { sadieSimulator } from "@/lib/sadieSimulator";
import { cn } from "@/lib/utils";
import { SadieSimulatorPromptChange } from "@/types";

// One change in the sidebar. It opens to its full text, which for a removal
// is the text it takes out. A change or an addition can be edited in place.
export const SadieSimulatorPromptChangeItem = ({
  change,
  isDisabled,
  onEdit,
  onRemove,
  removedText,
}: {
  change: SadieSimulatorPromptChange;
  isDisabled: boolean;
  onEdit: () => void;
  onRemove: () => void;
  // The section's text before this change takes it out. Only for a removal.
  removedText: string;
}) => {
  /* React Hooks */
  const [isExpanded, setIsExpanded] = useState(false);

  /* Helpers */
  const kind = sadieSimulator.changeKind(change);
  const isRemoval = kind === "REMOVE";
  const fullText = isRemoval ? removedText : change.text.trim();
  const kindLabel = () => {
    if (kind === "ADD") return `Added after ${change.after ?? ""}`;
    if (isRemoval) return "Removed";
    return "Changed";
  };

  /* Render */
  return (
    <Row className="items-start gap-1 rounded-md border border-slate-200 bg-white p-2">
      <Column className="min-w-0 flex-1 gap-0.5">
        <Text className="text-xs text-slate-700">{change.heading}</Text>
        <Text className="text-xs font-normal text-slate-500">
          {kindLabel()}
        </Text>
        {fullText ? (
          <>
            <Text
              className={cn(
                "whitespace-pre-wrap text-xs font-normal text-slate-500",
                !isExpanded && "line-clamp-2",
                isRemoval && "line-through",
              )}
            >
              {fullText}
            </Text>
            <Button
              className="h-auto self-start p-0 text-xs text-slate-500"
              onClick={() => {
                setIsExpanded(!isExpanded);
              }}
              variant="link"
            >
              {isExpanded ? "Show less" : "Show all"}
            </Button>
          </>
        ) : null}
      </Column>
      {isRemoval ? null : (
        <Button
          aria-label="Edit this change"
          className="h-6 w-6 shrink-0 text-slate-400"
          disabled={isDisabled}
          onClick={onEdit}
          size="icon"
          variant="ghost"
        >
          <PencilIcon className="h-3.5 w-3.5" />
        </Button>
      )}
      <Button
        aria-label="Remove this change"
        className="h-6 w-6 shrink-0 text-slate-400"
        disabled={isDisabled}
        onClick={onRemove}
        size="icon"
        variant="ghost"
      >
        <XIcon className="h-3.5 w-3.5" />
      </Button>
    </Row>
  );
};
